import { useCallback, useMemo } from 'react';
export function getRoutePath(route) {
  return route.path;
}
export function isRouteMatched(pathname, routePath) {
  const currentPath = pathname.split('?')[0];
  const currentParts = currentPath.split('/').filter(Boolean);
  const routeParts = routePath.split('/').filter(Boolean);
  if (currentParts.length !== routeParts.length) {
    return false;
  }
  return routeParts.every((part, index) => {
    if (part.startsWith(':')) {
      return true;
    }
    return part === currentParts[index];
  });
}
function normalizePath(path) {
  const nextPath = path.replace(/\/+/g, '/');
  if (nextPath === '/') {
    return nextPath;
  }
  return nextPath.replace(/\/$/, '');
}
function joinRoutePath(parentPath = '', routePath = '') {
  if (!routePath) {
    return parentPath || '/';
  }
  if (!parentPath) {
    return normalizePath(routePath.startsWith('/') ? routePath : `/${routePath}`);
  }
  if (routePath.startsWith(parentPath)) {
    return normalizePath(routePath);
  }
  const nextChildPath = routePath.startsWith('/') ? routePath : `/${routePath}`;
  return normalizePath(`${parentPath}${nextChildPath}`);
}
function getRouteKey(route, path) {
  if (route.key) {
    return route.key;
  }
  if (route.componentPath) {
    return route.componentPath;
  }
  return path.replace(/^\//, '') || route.name;
}
function normalizeRoutes(routeList, parentPath = '') {
  return routeList.map(route => {
    const currentPath = joinRoutePath(parentPath, route.path || '');
    const children = route.children || route.routes;
    return {
      ...route,
      key: getRouteKey(route, currentPath),
      path: currentPath,
      componentPath: route.componentPath || route.key,
      ignore: route.ignore,
      children: children?.length ? normalizeRoutes(children, currentPath) : undefined
    };
  });
}
const routeConfig = [{
  name: '系统首页',
  key: 'example',
  path: '/example',
  componentPath: 'example'
}, {
  name: '任务管理',
  key: 'todo',
  path: '/todo',
  routes: [{
    name: '待办任务',
    key: 'todo/task',
    path: '/task',
    componentPath: 'todo/task'
  }, {
    name: '已办任务',
    key: 'todo/completed',
    path: '/completed',
    componentPath: 'todo/completed'
  }, {
    name: '我发起的',
    key: 'todo/iinitiated',
    path: '/iinitiated',
    componentPath: 'todo/iinitiated'
  }]
}, {
  name: '行政管理',
  key: 'modules',
  path: '/modules',
  routes: [{
    name: '请假管理',
    key: 'modules/leave',
    path: '/leave',
    componentPath: 'modules/leave'
  }, {
    name: '物资管理',
    key: 'modules/materials',
    path: '/materials',
    componentPath: 'modules/materials'
  }, {
    name: '施工管理',
    key: 'modules/construction',
    path: '/construction',
    componentPath: 'construction',
    requireAuth: false
  }]
}, {
  name: '系统管理',
  key: 'system',
  path: '',
  requireAuth: true,
  routes: [{
    name: '权限管理',
    key: 'auth',
    path: '',
    routes: [{
      name: '用户管理',
      key: 'users',
      path: '/users',
      componentPath: 'users',
      requireAuth: true,
      routes: [{
        name: '用户详情',
        key: 'users/UserView',
        path: '/detail/:userId',
        componentPath: 'users/UserView',
        ignore: true,
        requireAuth: true
      }]
    }, {
      name: '角色管理',
      key: 'roles',
      path: '/roles',
      componentPath: 'roles',
      requireAuth: true,
      routes: [{
        name: '角色详情',
        key: 'roles/RoleView',
        path: '/detail/:roleId',
        componentPath: 'roles/RoleView',
        ignore: true,
        requireAuth: true
      }]
    }]
  }, {
    name: '组织管理',
    key: 'org',
    path: '',
    routes: [{
      name: '组织架构',
      key: 'org/index',
      path: '/org',
      componentPath: 'org/index',
      requireAuth: true
    }, {
      name: '部门管理',
      key: 'department',
      path: '/org/department',
      componentPath: 'department',
      exact: false,
      requireAuth: true,
      routes: [{
        name: '人员详情',
        key: 'department/PersonView',
        path: '/person/:personId',
        componentPath: 'department/PersonView',
        ignore: true,
        requireAuth: true
      }]
    }, {
      name: '岗位管理',
      key: 'position',
      path: '/org/position',
      componentPath: 'position',
      requireAuth: true
    }]
  }, {
    name: '数据字典',
    key: 'dict',
    path: '/dicts',
    componentPath: 'dicts',
    requireAuth: true,
    routes: [{
      name: '枚举项管理',
      key: 'dicts/DictItemsView',
      path: '/items/:dictId',
      componentPath: 'dicts/DictItemsView',
      ignore: true,
      requireAuth: true
    }]
  }, {
    name: '菜单管理',
    key: 'menu',
    path: '/menu',
    componentPath: 'menu'
  }, {
    name: '文件管理',
    key: 'files',
    path: '/files',
    componentPath: 'files',
    requireAuth: true
  }]
}, {
  name: '千人千面',
  key: 'drag',
  path: '/drag',
  requireAuth: false,
  ignore: true,
  routes: [{
    name: '拖拽布局',
    key: 'drag/layout',
    path: '/layout',
    componentPath: 'drag-layout',
    requireAuth: false
  }, {
    name: '布局预览',
    key: 'drag/layout-preview',
    path: '/layout-preview',
    componentPath: 'drag-layout-preview',
    requireAuth: false
  }]
}, {
  name: '流程设计',
  key: 'flow/design',
  path: '/flow/design',
  requireAuth: true,
  routes: [{
    // 默认（无子路径）渲染流程管理列表页
    name: '流程管理',
    key: 'flow/design/list',
    path: '',
    componentPath: 'flow/design/list',
    requireAuth: true
  }, {
    // 设计器：/flow/design/editor         新增
    //         /flow/design/editor/:id    编辑现有流程
    name: '流程编辑器',
    key: 'flow/design/editor',
    path: 'editor/:id?',
    componentPath: 'flow/design',
    ignore: true,
    requireAuth: true
  }]
}, {
  name: '智能表单',
  key: 'ai',
  path: '/ai',
  requireAuth: true,
  routes: [{
    name: '模板管理',
    key: 'ai/template',
    path: '/template',
    componentPath: 'tpl/template',
    requireAuth: true,
    routes: [{
      name: '模板详情',
      key: 'ai/template/view',
      path: '/view/:id',
      componentPath: 'tpl/template/view',
      ignore: true,
      requireAuth: true
    }, {
      name: '模板变量',
      key: 'ai/template/vars',
      path: '/vars/:id',
      componentPath: 'tpl/template/vars',
      ignore: true,
      requireAuth: true
    }]
  }, {
    name: '表单管理',
    key: 'ai/form',
    path: '/form',
    componentPath: 'tpl/form',
    requireAuth: true
  }]
}, {
  name: '个人中心',
  key: 'personal',
  path: 'personal',
  componentPath: 'personal'
}];
export const routes = normalizeRoutes(routeConfig);
export const getName = (path, routes) => {
  return routes.find(item => {
    const itemPath = getRoutePath(item);
    if (isRouteMatched(path, itemPath)) {
      return item.name;
    } else if (item.children) {
      return getName(path, item.children);
    }
  });
};
export function findMenuKeyByPathname(pathname, routeList = routes) {
  const currentPath = pathname.split('?')[0];
  for (const route of routeList) {
    if (route.children?.length) {
      const childKey = findMenuKeyByPathname(currentPath, route.children);
      if (childKey) {
        return childKey;
      }
    }
    const routePath = getRoutePath(route);
    if (routePath && isRouteMatched(currentPath, routePath)) {
      return route.key;
    }
  }
  return '';
}
export const generatePermission = role => {
  const actions = role === 'admin' ? ['*'] : ['read'];
  const result = {};
  function travel(routeList) {
    routeList.forEach(item => {
      if (item.children?.length) {
        travel(item.children);
        return;
      }
      result[item.name] = actions;
    });
  }
  travel(routes);
  return result;
};
const useRoute = (menuKeys, isAdmin = false) => {
  const menuKeySet = useMemo(() => new Set(menuKeys ?? []), [menuKeys]);
  const filterRoute = useCallback((routeList, arr = []) => {
    if (!routeList.length) {
      return [];
    }
    for (const route of routeList) {
      // admin 全部显示；menuKeys 未加载时不拦截；requireAuth=true 时由后端 menuKeys 控制显隐
      const visible = isAdmin || !route.requireAuth || menuKeys == null || menuKeySet.has(route.key);
      if (!visible) {
        continue;
      }
      if (route.children && route.children.length) {
        const newRoute = {
          ...route,
          children: []
        };
        filterRoute(route.children, newRoute.children);
        if (newRoute.children.length) {
          arr.push(newRoute);
        }
      } else {
        arr.push({
          ...route
        });
      }
    }
    return arr;
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [menuKeys, menuKeySet]);
  const permissionRoute = useMemo(() => filterRoute(routes, []), [filterRoute]);
  const defaultRoute = useMemo(() => {
    function findFirstRoute(routeList) {
      for (const route of routeList) {
        if (route.children?.length) {
          const childPath = findFirstRoute(route.children);
          if (childPath) {
            return childPath;
          }
        } else {
          return getRoutePath(route);
        }
      }
      return '';
    }
    return findFirstRoute(permissionRoute);
  }, [permissionRoute]);
  return [permissionRoute, defaultRoute];
};
export default useRoute;